/**
 * CreateBatchForm Component
 *
 * Modal form for creating a new batch with optional name input
 * Auto-generates name if left empty
 */

import React, { useState } from 'react';
import { BatchCreate } from '../../types/batch';

interface CreateBatchFormProps {
  projectId: string;
  onSubmit: (data: BatchCreate) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const CreateBatchForm: React.FC<CreateBatchFormProps> = ({
  projectId,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [name, setName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: name.trim() || undefined,
      project_id: projectId,
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Batch</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div>
            <label
              htmlFor="batch-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Batch Name <span className="text-gray-500">(Optional)</span>
            </label>
            <input
              type="text"
              id="batch-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
              className="block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              placeholder="e.g., Marketing Campaign Test"
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave empty to auto-generate a name based on current date and time
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading && (
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              Create Batch
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
